import { useId, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { RemixiconComponentType } from '@remixicon/react';
import { ApiError } from '@/shared/api/client';
import { Button, confirm, Field, FieldCaption, IconPicker, Input, Select, SidePanel, toast } from '@/shared/ui';
import { useHotkey } from '@/features/settings/hotkeys';
import { AUTO_COLOR, colorFromValue, colorValue, useColorOptions } from './colors';

/** Glyphs offered for a device category (charts tree, monitoring menu) — the seeded families plus generic sensors. */
const CATEGORY_ICONS = [
  'temp-hot-line',
  'thermometer-line',
  'drop-line',
  'contrast-drop-line',
  'water-flash-line',
  'dashboard-3-line',
  'dashboard-2-line',
  'compass-3-line',
  'ruler-line',
  'arrow-left-right-line',
  'focus-3-line',
  'sensor-line',
] as const;

export interface NameDraft {
  name: string;
  icon?: string | null;
  color?: string | null;
}

/** A copy of a field-error map without `key` (clears one error as the field changes). */
export function withoutKey<T extends Record<string, unknown>>(map: T, key: string): T {
  const next = { ...map };
  delete next[key];
  return next;
}

/** Esc closes a docked panel unless it comes from an open popup (select list, menu, dialog) inside it. */
export function panelEscape(onClose: () => void) {
  return (e: KeyboardEvent<HTMLElement>) => {
    if (e.key !== 'Escape' || e.defaultPrevented) return;
    const target = e.target as HTMLElement;
    if (target.closest('[role="listbox"], [role="menu"], [role="dialog"], [aria-expanded="true"]')) return;
    e.preventDefault();
    onClose();
  };
}

/** Ask before dropping unsaved input ("O'zgarishlar saqlanmagan", 03-modules §2.3). */
export async function confirmDiscard(t: (key: string) => string) {
  return confirm({
    title: t('common.unsavedTitle'),
    description: t('common.unsavedText'),
    confirmLabel: t('common.leave'),
    cancelLabel: t('common.stay'),
    tone: 'danger',
    icon: null,
  });
}

/**
 * Create / edit side panel with one name field (585:14707 "Qurilma turkumini yaratish", 586:15488 "Joylashuv o'rnini
 * yaratish"): 300 w, "Ortga qaytish", chip 32 + title, caps label + field, footer "Bekor qilish" | "Saqlash".
 * Names are required, ≤ `maxLength`, unique per tenant (case-insensitive; the API answers 409 `name_taken`).
 * With `appearance` (categories, [P] 03-modules §4.3) the optional icon and colour used by the charts tree and the
 * monitoring menu are offered below the name.
 */
export function NamePanel({
  title,
  icon,
  label,
  placeholder,
  initialName,
  appearance,
  maxLength,
  existingNames,
  onSave,
  onClose,
}: {
  title: string;
  icon: RemixiconComponentType;
  label: string;
  placeholder: string;
  initialName: string;
  /** offer the category icon + colour (initial values) */
  appearance?: { icon: string | null; color: string | null };
  maxLength: number;
  /** names already taken (other rows) — checked before sending */
  existingNames: readonly string[];
  onSave: (draft: NameDraft) => Promise<unknown>;
  onClose: () => void;
}) {
  const { t } = useTranslation('devices');
  const formId = useId();
  const [name, setName] = useState(initialName);
  const [glyph, setGlyph] = useState(appearance?.icon ?? null);
  const [color, setColor] = useState(appearance?.color ?? null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const dirty = name.trim() !== initialName.trim() || (appearance != null && (glyph !== appearance.icon || color !== appearance.color));
  const colorOptions = useColorOptions(appearance?.color);

  const close = async () => {
    if (saving) return;
    if (dirty && !(await confirmDiscard(t))) return;
    onClose();
  };

  const submit = async () => {
    const value = name.trim();
    if (!value) return setError(t('common.required'));
    const lower = value.toLocaleLowerCase('uz');
    if (existingNames.some((n) => n.trim().toLocaleLowerCase('uz') === lower)) return setError(t('common.nameTaken'));
    setSaving(true);
    try {
      await onSave(appearance ? { name: value, icon: glyph, color } : { name: value });
      toast.success(t('common.saved'));
      onClose();
    } catch (err) {
      if (err instanceof ApiError && err.code === 'name_taken') setError(t('common.nameTaken'));
      else if (err instanceof ApiError && err.fieldErrors.name) setError(err.fieldErrors.name);
      else toast.error(t('common.error'), { subtitle: err instanceof Error ? err.message : undefined });
    } finally {
      setSaving(false);
    }
  };

  useHotkey('save', () => void submit());

  return (
    <SidePanel
      width={300}
      onBack={() => void close()}
      backLabel={t('common.back')}
      icon={{ icon, color: 'darkBlue' }}
      title={title}
      aria-label={title}
      footer={
        <>
          <Button variant="secondary" className="flex-none!" onClick={() => void close()} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary-alt" type="submit" form={formId} loading={saving}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      {/* oxlint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- Esc inside the docked panel's form closes the panel (open popups inside handle their own Esc) */}
      <form
        id={formId}
        noValidate
        onKeyDown={panelEscape(() => void close())}
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <Field label={label} error={error}>
          <Input
            // oxlint-disable-next-line jsx-a11y/no-autofocus -- the panel opens from an explicit action; its only field takes focus
            autoFocus
            aria-required
            value={name}
            maxLength={maxLength}
            placeholder={placeholder}
            onChange={(e) => {
              setName(e.currentTarget.value);
              if (error) setError(null);
            }}
          />
        </Field>
        {appearance && (
          <div className="mt-14 flex flex-col gap-14">
            <div className="flex flex-col gap-8">
              <FieldCaption as="span">{t('categories.icon')}</FieldCaption>
              <IconPicker size="sm" icons={CATEGORY_ICONS} value={glyph} onValueChange={setGlyph} aria-label={t('categories.icon')} />
            </div>
            <Field label={t('categories.color')}>
              <Select<string> options={colorOptions} value={colorValue(color)} onValueChange={(v) => setColor(colorFromValue(v ?? AUTO_COLOR))} />
            </Field>
          </div>
        )}
      </form>
    </SidePanel>
  );
}
